import {
  Box,
  Button,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  useColorModeValue,
} from '@chakra-ui/react';

type SnapshotDeleteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  yourCompanyName: string;
  otherCompanyName: string;
  hasResults: boolean;
};

export function SnapshotDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
  yourCompanyName,
  otherCompanyName,
  hasResults,
}: SnapshotDeleteModalProps) {
  const textColor = useColorModeValue('navy.700', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="30px" mx={4}>
        <ModalHeader pb={2}>
          <Text fontSize="xl" fontWeight="700" color={textColor}>
            Remove your account list?
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={0}>
          <Text color={muted} fontSize="sm" lineHeight="1.65" mb={4}>
            You are in control. You can take{' '}
            <Text as="span" fontWeight="600" color={textColor}>
              {yourCompanyName}
            </Text>
            &apos;s list out of this snapshot at any time.{' '}
            <Text as="span" fontWeight="600" color={textColor}>
              {otherCompanyName}
            </Text>
            &apos;s data stays exactly as it is, and they can keep working from their side.
          </Text>
          <Text fontSize="sm" fontWeight="600" color={textColor} mb={2}>
            We will remove
          </Text>
          <UnorderedList spacing={2} pl={1} fontSize="sm" color={muted} mb={4}>
            <ListItem>The account list your team uploaded for this snapshot</ListItem>
            <ListItem>Any remaining copies of that file we still hold for this snapshot</ListItem>
          </UnorderedList>
          {hasResults && (
            <>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                You keep the good stuff
              </Text>
              <UnorderedList spacing={2} pl={1} fontSize="sm" color={muted}>
                <ListItem>
                  Your overlap insights and playbook views, alongside {otherCompanyName}
                </ListItem>
                <ListItem>{otherCompanyName}&apos;s account list, unchanged</ListItem>
              </UnorderedList>
            </>
          )}
          <Box
            mt={4}
            p={3}
            borderRadius="12px"
            bg="gray.50"
            fontSize="sm"
            color={muted}
            lineHeight="1.55"
          >
            {hasResults
              ? 'Your list goes away, but you can still come back to this snapshot to explore where you overlap. Those results stay available for 14 days after they were first ready.'
              : 'Overlap has not been calculated yet. If you remove your list now, this snapshot may close when there is nothing left to compare.'}
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={onConfirm}
            isLoading={isDeleting}
            loadingText="Removing..."
          >
            Remove my list
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
